import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Shield, Trash2, Activity as ActivityIcon, Search, Users, Crown, Pause, Play, UserPlus, Mail, X, Copy, Clock, Eye } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "member" });
  const [invites, setInvites] = useState([]);
  const [seats, setSeats] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const isMaster = me?.role === "master";
  const PROTECTED_MASTER_EMAILS = [
    "colibrisocialclub@gmail.com",
    "contact@amanbiswalthakur.in",
    "thyzfoto@gmail.com",
    "admin@weddingsthyzfoto.com"
  ];
  const isProtectedMaster = (email) => PROTECTED_MASTER_EMAILS.includes((email || "").toLowerCase().trim());

  const load = async () => {
    const [u, a, s, inv, seat] = await Promise.all([
      api.get("/admin/users"),
      api.get("/admin/activity"),
      api.get("/admin/stats"),
      api.get("/team/invites").catch(() => ({ data: [] })),
      api.get("/team/seats").catch(() => ({ data: null })),
    ]);
    setUsers(u.data); setActivity(a.data); setStats(s.data);
    setInvites(inv.data || []); setSeats(seat.data);
  };
  useEffect(() => { load(); }, []);

  const setRole = async (uid, role) => {
    try { await api.patch(`/admin/users/${uid}`, { role }); toast.success(`Role → ${role}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setPlan = async (uid, plan) => {
    try { await api.patch(`/admin/users/${uid}`, { plan }); toast.success(`Plan → ${plan}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setSuspended = async (uid, suspended, email) => {
    if (suspended && isProtectedMaster(email)) {
      toast.error("Master administrator account cannot be suspended");
      return;
    }
    try { await api.patch(`/admin/users/${uid}`, { suspended }); toast.success(suspended ? "Suspended" : "Reactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeUser = async (uid, email) => {
    if (isProtectedMaster(email)) {
      toast.error("Master administrator account is permanent and cannot be deleted");
      return;
    }
    if (!window.confirm("Delete this user permanently?")) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addTeamMember = async () => {
    if (!inviteForm.email) { toast.error("Email required"); return; }
    setInviteSending(true);
    try {
      const { data } = await api.post("/team/invite", inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setLastLink(data.invite_link || "");
      setInviteForm({ email: "", name: "", role: "member" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setInviteSending(false); }
  };

  const revokeInvite = async (iid) => {
    if (!window.confirm("Revoke this invitation?")) return;
    try {
      await api.delete(`/team/invites/${iid}`);
      toast.success("Invitation revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyLink = (link) => {
    const full = link.startsWith("http") ? link : `${window.location.origin}${link}`;
    navigator.clipboard.writeText(full).then(() => toast.success("Invite link copied"));
  };

  const filtered = users.filter(u => !q || u.email.toLowerCase().includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 space-y-6" data-testid="admin-page">
      <div className="rise flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--brand-dim)" }}>
          <Shield className="w-5 h-5" style={{ color: "var(--brand)" }} />
        </div>
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>{isMaster ? "Master Panel" : "Team Panel"}</h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--text-soft)" }}>{isMaster ? "Total control across everything and everyone." : "Manage your team members."}</p>
        </div>
        <span className="px-2.5 py-1 rounded-full text-xs font-bold tracking-widest"
          style={{ background: isMaster ? "rgba(255,77,0,0.1)" : "var(--brand-dim)", color: isMaster ? "#FF4D00" : "var(--brand)" }}>
          {isMaster ? "MASTER" : "ADMIN"}
        </span>
        <div className="ml-auto flex gap-2">
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-team-btn" className="text-white gap-2" style={{ background: "var(--brand)" }}>
                <UserPlus className="w-4 h-4" /> Invite {isMaster ? "user" : "teammate"}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="invite-dialog">
              <DialogHeader>
                <DialogTitle>Invite teammate</DialogTitle>
                <DialogDescription>They&apos;ll get an email with a magic link to set their password and join {me?.business_name || "your workspace"}.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label>Email</Label><Input data-testid="invite-form-email" type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="teammate@company.com" /></div>
                <div><Label>Name (optional)</Label><Input data-testid="invite-form-name" value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="Alex Rivera" /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                    <SelectTrigger data-testid="invite-form-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member — can manage day-to-day marketing</SelectItem>
                      <SelectItem value="admin">Admin — can invite + manage others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {seats && seats.seats_limit !== -1 && (
                  <div className="text-xs" style={{ color: "var(--text-soft)" }}>
                    Seats used: {seats.seats_used}/{seats.seats_limit} · {seats.pending_invites} pending invite{seats.pending_invites === 1 ? "" : "s"}
                  </div>
                )}
                {lastLink && (
                  <div className="p-3 rounded-xl text-xs flex items-center gap-2" style={{ background: "color-mix(in srgb, var(--brand) 10%, var(--surface))", border: "1px solid var(--border)" }}>
                    <span className="truncate flex-1 font-sans" style={{ color: "var(--text)" }}>{lastLink}</span>
                    <Button size="sm" variant="ghost" onClick={() => copyLink(lastLink)} data-testid="copy-last-link"><Copy className="w-3 h-3" /></Button>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="invite-send" onClick={addTeamMember} disabled={inviteSending} className="text-white" style={{ background: "var(--brand)" }}>
                  {inviteSending ? "Sending…" : "Send invite"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="rise card-premium p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="admin-stat-users">
            <div className="text-xs uppercase tracking-wider font-semibold font-sans mb-1" style={{ color: "var(--text-soft)" }}>Users</div>
            <div className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>{stats.total_users}</div>
            <div className="text-xs mt-1" style={{ color: "var(--text-xsoft)" }}>+{stats.signups_today} today</div>
          </Card>
          <Card className="rise card-premium p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="admin-stat-admins">
            <div className="text-xs uppercase tracking-wider font-semibold font-sans mb-1" style={{ color: "var(--text-soft)" }}>Admins</div>
            <div className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--brand)" }}>{stats.admins}</div>
          </Card>
          <Card className="rise card-premium p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="admin-stat-suspended">
            <div className="text-xs uppercase tracking-wider font-semibold font-sans mb-1" style={{ color: "var(--text-soft)" }}>Suspended</div>
            <div className="text-3xl font-bold" style={{ color: "#EF4444" }}>{stats.suspended}</div>
          </Card>
          <Card className="rise card-premium p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="admin-stat-activities">
            <div className="text-xs uppercase tracking-wider font-semibold font-sans mb-1" style={{ color: "var(--text-soft)" }}>Activities</div>
            <div className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>{stats.activities}</div>
          </Card>
        </div>
      )}

      <Tabs defaultValue="users">
        <TabsList style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1" /> {isMaster ? "All Users" : "My Team"}</TabsTrigger>
          <TabsTrigger value="invites" data-testid="admin-tab-invites"><Mail className="w-4 h-4 mr-1" /> Invitations{invites.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs" style={{ background: "rgba(255,77,0,0.1)", color: "#FF4D00" }}>{invites.length}</span>}</TabsTrigger>
          <TabsTrigger value="activity" data-testid="admin-tab-activity"><ActivityIcon className="w-4 h-4 mr-1" /> Activity Log</TabsTrigger>
          {isMaster && <TabsTrigger value="impersonation" data-testid="admin-tab-impersonation"><Eye className="w-4 h-4 mr-1" /> Impersonation</TabsTrigger>}
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <Card className="card-premium overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-xsoft)" }} />
                <Input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="pl-9" style={{ background: "var(--bg)" }} />
              </div>
              <div className="ml-auto text-sm" style={{ color: "var(--text-soft)" }}>{filtered.length} of {users.length}</div>
            </div>
            <Table>
              <TableHeader>
                <TableRow style={{ background: "var(--bg)" }}>
                  <TableHead style={{ color: "var(--text-soft)" }}>User</TableHead>
                  <TableHead style={{ color: "var(--text-soft)" }}>Email</TableHead>
                  <TableHead style={{ color: "var(--text-soft)" }}>Role</TableHead>
                  {isMaster && <TableHead style={{ color: "var(--text-soft)" }}>Plan</TableHead>}
                  <TableHead style={{ color: "var(--text-soft)" }}>Status</TableHead>
                  <TableHead style={{ color: "var(--text-soft)" }}>Last login</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u, i) => (
                  <TableRow key={u.id} data-testid={`admin-user-row-${i}`} className="hover:opacity-90 transition-colors" style={{ borderColor: "var(--border)" }}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-xs font-semibold" style={{ background: "var(--brand)" }}>
                          {(u.name || u.email)[0].toUpperCase()}
                        </div>
                        <span className="font-semibold">{u.name}</span>
                        {u.role === "master" && <Crown className="w-3 h-3 text-amber-500" />}
                        {u.role === "admin" && <Shield className="w-3 h-3 text-[#0044FF]" />}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">{u.email}</TableCell>
                    <TableCell>
                      <Select disabled={u.id === me.id || !isMaster} value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                        <SelectTrigger className="w-32" data-testid={`admin-role-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="master">Master</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    {isMaster && (
                      <TableCell>
                        <Select value={u.plan || "free"} onValueChange={(v) => setPlan(u.id, v)}>
                          <SelectTrigger className="w-24" data-testid={`admin-plan-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="scale">Scale</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch data-testid={`admin-suspend-${i}`} checked={!u.suspended} disabled={u.id === me.id} onCheckedChange={(v) => setSuspended(u.id, !v)} />
                        <Badge className={u.suspended ? "bg-orange-100 text-[#FF4D00]" : "bg-emerald-100 text-emerald-700"}>
                          {u.suspended ? <><Pause className="w-3 h-3 mr-1" /> Suspended</> : <><Play className="w-3 h-3 mr-1" /> Active</>}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 mono">{u.last_login ? new Date(u.last_login).toLocaleString() : "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          data-testid={`impersonate-${i}`}
                          variant="ghost"
                          size="icon"
                          title="View this workspace"
                          disabled={u.id === me.id}
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/admin/impersonate/${u.id}`);
                              localStorage.setItem("ld_token", data.token);
                              toast.success(`Now viewing ${data.viewing_as.email}'s workspace`);
                              window.location.href = "/";
                            } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                          }}
                        ><Eye className="w-4 h-4 text-[#0044FF]" /></Button>
                        {isProtectedMaster(u.email) ? (
                          <div className="p-2 text-emerald-600 dark:text-emerald-400" title="Protected Master Account (Permanent)">
                            <Shield className="w-4 h-4" />
                          </div>
                        ) : (
                          <Button data-testid={`admin-delete-${i}`} variant="ghost" size="icon" disabled={u.id === me.id || (!isMaster && u.parent_id !== me.id)} onClick={() => removeUser(u.id, u.email)}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow><TableCell colSpan={isMaster ? 7 : 6} className="text-center py-10 text-slate-400">No users</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="invites" className="mt-4">
          <Card className="border-slate-200" data-testid="invites-card">
            <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
              <div>
                <div className="text-sm font-semibold">Pending invitations</div>
                <div className="text-xs" style={{ color: "var(--text-soft)" }}>Invites that haven&apos;t been accepted yet</div>
              </div>
              {seats && (
                <div className="ml-auto text-xs px-3 py-1 rounded-full" style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)" }}>
                  {seats.seats_limit === -1 ? "Unlimited seats" : `${seats.seats_used}/${seats.seats_limit} seats used`} · {invites.length} pending
                </div>
              )}
            </div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Invited by</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {invites.map((inv, i) => (
                  <TableRow key={inv.id} data-testid={`invite-row-${i}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{inv.email}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{inv.role}</Badge></TableCell>
                    <TableCell className="text-sm" style={{ color: "var(--text-soft)" }}>{inv.inviter_email}</TableCell>
                    <TableCell className="text-xs mono" style={{ color: "var(--text-soft)" }}>{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-xs mono flex items-center gap-1" style={{ color: "var(--text-soft)" }}><Clock className="w-3 h-3" />{new Date(inv.expires_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button data-testid={`invite-copy-${i}`} variant="ghost" size="icon" title="Copy invite link" onClick={() => copyLink(`/invite/${inv.token || ""}`)} disabled><Copy className="w-4 h-4 text-slate-400" /></Button>
                        <Button data-testid={`invite-revoke-${i}`} variant="ghost" size="icon" title="Revoke" onClick={() => revokeInvite(inv.id)}><X className="w-4 h-4 text-red-400" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {invites.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-12" style={{ color: "var(--text-soft)" }}>
                    <Mail className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <div className="text-sm">No pending invitations</div>
                    <div className="text-xs mt-1">Invite teammates to collaborate on campaigns</div>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <Card className="border-slate-200">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>When</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Detail</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {activity.map((a, i) => (
                  <TableRow key={a.id} data-testid={`admin-activity-${i}`}>
                    <TableCell className="text-xs text-slate-500 mono whitespace-nowrap">{new Date(a.ts).toLocaleString()}</TableCell>
                    <TableCell className="text-sm">{a.user_email || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className="border-blue-200 bg-blue-50 text-[#0044FF] mono text-xs">{a.action}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500 mono">{a.meta && Object.keys(a.meta).length ? JSON.stringify(a.meta) : "—"}</TableCell>
                  </TableRow>
                ))}
                {activity.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-400">No activity yet</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {isMaster && (
          <TabsContent value="impersonation" className="mt-4">
            <ImpersonationLog />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function ImpersonationLog() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/impersonation-log").then(({ data }) => setEvents(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  // Pair start→end into sessions
  const sessions = [];
  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    if (e.action === "impersonate_end") {
      const start = events.slice(i + 1).find(x => x.action === "impersonate_start" && x.user_email === e.user_email);
      sessions.push({ actor: e.user_email, start: start?.at, end: e.at, target: start?.details?.target_email });
    }
  }
  const opens = events.filter(e => e.action === "impersonate_start" && !events.slice(0, events.indexOf(e)).some(x => x.action === "impersonate_end" && x.user_email === e.user_email));

  return (
    <Card className="border-slate-200" data-testid="impersonation-log-card">
      <div className="p-4 border-b" style={{ borderColor: "var(--border)" }}>
        <div className="text-sm font-semibold">Impersonation history</div>
        <div className="text-xs" style={{ color: "var(--text-soft)" }}>Every admin/master session where someone viewed another user&apos;s workspace</div>
      </div>
      <Table>
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead>Actor</TableHead>
          <TableHead>Viewed as</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Ended</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {opens.map((e, i) => (
            <TableRow key={`open-${i}`} className="hover:bg-slate-50" data-testid={`imp-open-${i}`}>
              <TableCell className="font-medium">{e.user_email}</TableCell>
              <TableCell className="text-sm" style={{ color: "var(--text-soft)" }}>{e.details?.target_email}</TableCell>
              <TableCell className="text-xs mono">{new Date(e.at).toLocaleString()}</TableCell>
              <TableCell><Badge variant="outline" className="text-amber-700 bg-amber-50 border-amber-300">Active</Badge></TableCell>
            </TableRow>
          ))}
          {sessions.map((s, i) => (
            <TableRow key={`s-${i}`} className="hover:bg-slate-50" data-testid={`imp-session-${i}`}>
              <TableCell className="font-medium">{s.actor}</TableCell>
              <TableCell className="text-sm" style={{ color: "var(--text-soft)" }}>{s.target || "—"}</TableCell>
              <TableCell className="text-xs mono">{s.start ? new Date(s.start).toLocaleString() : "—"}</TableCell>
              <TableCell className="text-xs mono">{new Date(s.end).toLocaleString()}</TableCell>
            </TableRow>
          ))}
          {loading && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>}
          {!loading && sessions.length + opens.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10" style={{ color: "var(--text-soft)" }}>No impersonation activity yet</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}
